import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Content from '../../components/Content';
import NetworkError from '../../components/Error';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import Modal from '../../components/Modal';
import ModalHeading from '../../components/Modal/ModalHeading';
import styles from './NetZones.module.css';

const FRONTEND_NET_ZONES_ERROR_CODES = {
    INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
    UPDATE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
    DELETE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildNetZonesSupportError = (errorCode, pageInfo, sourceMessage) =>
    formatFrontendSupportError({
        errorCode,
        pageInfo,
        sourceMessage,
    });

const NetZones = () => {
    const [netZonesData, setNetZonesData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [supportErrorMessage, setSupportErrorMessage] = useState('');
    const [copiedSupportError, setCopiedSupportError] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState('ALL');
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const [userCompany, setUserCompany] = useState('');
    const [isProvider, setIsProvider] = useState(false);
    
    // Modal states for editing
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingZone, setEditingZone] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingZone, setDeletingZone] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshMsg, setRefreshMsg] = useState('');

    const setNetZonesSupportError = useCallback((errorCode, pageInfo, sourceMessage) => {
        setSupportErrorMessage(buildNetZonesSupportError(errorCode, pageInfo, sourceMessage));
        setCopiedSupportError(false);
    }, []);

    const clearNetZonesSupportError = useCallback((force = false) => {
        if (!force) return;
        setSupportErrorMessage('');
        setCopiedSupportError(false);
    }, []);

    const handleCopySupportError = useCallback(async () => {
        if (!supportErrorMessage) return;
        try {
            await navigator.clipboard.writeText(supportErrorMessage);
            setCopiedSupportError(true);
            setTimeout(() => setCopiedSupportError(false), 2000);
        } catch (copyError) {
            setCopiedSupportError(false);
        }
    }, [supportErrorMessage]);

    // Create a simple action for fetching NetZones data
    const getNetZonesData = useCallback(async (companyFilter = 'ALL') => {
        try {
            let url = '/api/net_zones';
            if (companyFilter !== 'ALL') {
                url += `?company=${companyFilter}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            let data = {};
            try {
                data = await response.json();
            } catch (parseError) {
                data = {};
            }

            if (!response.ok) {
                return {
                    status: false,
                    msg: data?.msg || `NetZones request failed with HTTP ${response.status}`,
                };
            }
            return data;
        } catch (error) {
            console.error('Error fetching NetZones data:', error);
            return { status: false, msg: error.message };
        }
    }, []);

    const fetchData = useCallback(async (companyFilter = 'ALL') => {
        setIsLoading(true);
        setError(null);
        
        const data = await getNetZonesData(companyFilter);
        
        if (data && data.status) {
            setColumns(data.netZonesCols || []);
            setNetZonesData(data.netZonesRows || []);
            setAvailableCompanies(data.availableCompanies || []);
            setUserCompany(data.userCompany || '');
            setIsProvider(Boolean(data.isProvider));
            clearNetZonesSupportError(true);
            
        } else {
            const sourceMessage = data?.msg || 'Failed to fetch NetZones data.';
            setError(sourceMessage);
            setNetZonesSupportError(
                FRONTEND_NET_ZONES_ERROR_CODES.INITIAL_LOAD_FAILED,
                'NetZones Page / Initial Data Load Failed',
                sourceMessage
            );
        }
        
        setIsLoading(false);
    }, [clearNetZonesSupportError, getNetZonesData, setNetZonesSupportError]);

    useEffect(() => {
        fetchData(selectedCompany);
    }, [fetchData, selectedCompany]);

    const handleCompanyChange = (event) => {
        const newCompany = event.target.value;
        setSelectedCompany(newCompany);
    };

    const handleEdit = (zone, index) => {
        
        // Map based on actual column positions
        const zoneData = {
            ID: zone[0],        // id
            Company: zone[1],   // company
            StartIP: zone[2],   // StartIP
            EndIP: zone[3],     // EndIP
            Zone: zone[4],      // Zone
            Color: zone[5] || '#176576',  // Color (index 5)
            Guest: zone[6] === true || zone[6] === 'true' || zone[6] === 'Yes',  // Guest (index 6)
            index
        };
        
        setEditingZone(zoneData);
        setShowEditModal(true);
    };

    const handleDelete = (zone, index) => {
        
        // Based on the database structure and backend query:
        // columns = ["ID", "Company", "StartIP", "EndIP", "Zone"]
        // zone = [id, company, StartIP, EndIP, Zone]
        const zoneData = {
            id: zone[0],        // ID is at index 0
            company: zone[1],   // Company is at index 1  
            index
        };
        
        setDeletingZone(zoneData);
        setShowDeleteModal(true);
    };

    const handleUpdateZone = async (updatedZone) => {
        try {
            const response = await fetch('/api/net_zones', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedZone),
            });

            let result = {};
            try {
                result = await response.json();
            } catch (parseError) {
                result = {};
            }
            
            if (response.ok && result.status) {
                await fetchData(selectedCompany);
                setShowEditModal(false);
                setEditingZone(null);
                clearNetZonesSupportError(true);
            } else {
                setNetZonesSupportError(
                    FRONTEND_NET_ZONES_ERROR_CODES.UPDATE_FAILED,
                    'NetZones Page / Update NetZone Failed',
                    result?.msg || `Update NetZone request failed with HTTP ${response.status}`
                );
            }
        } catch (error) {
            console.error('Error updating NetZone:', error);
            setNetZonesSupportError(
                FRONTEND_NET_ZONES_ERROR_CODES.UPDATE_FAILED,
                'NetZones Page / Update NetZone Request Error',
                error?.message || String(error)
            );
        }
    };

    const handleDeleteZone = async () => {
        try {
            const deleteData = { 
                id: deletingZone.id,
                company: deletingZone.company 
            };
            
            
            const response = await fetch('/api/net_zones', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(deleteData),
            });
            let result = {};
            try {
                result = await response.json();
            } catch (parseError) {
                result = {};
            }
            
            if (response.ok && result.status === true) {
                await fetchData(selectedCompany);
                setShowDeleteModal(false);
                setDeletingZone(null);
                clearNetZonesSupportError(true);
            } else {
                console.error('Backend reports failure:', result.msg);
                setNetZonesSupportError(
                    FRONTEND_NET_ZONES_ERROR_CODES.DELETE_FAILED,
                    'NetZones Page / Delete NetZone Failed',
                    result?.msg || `Delete NetZone request failed with HTTP ${response.status}`
                );
            }
        } catch (error) {
            console.error('Error in handleDeleteZone:', error);
            console.error('Error stack:', error.stack);
            setNetZonesSupportError(
                FRONTEND_NET_ZONES_ERROR_CODES.DELETE_FAILED,
                'NetZones Page / Delete NetZone Request Error',
                error?.message || String(error)
            );
        }
    };

    // Re-apply the current zones to existing assets in the database. An asset's
    // zone otherwise only updates when its appliance next re-observes it, so a
    // freshly added zone would not reach devices already on record.
    const handleRefreshZones = async () => {
        setRefreshing(true);
        setRefreshMsg('');
        try {
            const response = await fetch('/api/netzone/refresh', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            let result = {};
            try { result = await response.json(); } catch (e) { result = {}; }
            if (response.ok && result.status === true) {
                setRefreshMsg(result.msg || 'Zones updated.');
                clearNetZonesSupportError(true);
            } else {
                setNetZonesSupportError(
                    FRONTEND_NET_ZONES_ERROR_CODES.UPDATE_FAILED,
                    'NetZones Page / Update Zones Failed',
                    result?.msg || `Update Zones request failed with HTTP ${response.status}`
                );
            }
        } catch (error) {
            setNetZonesSupportError(
                FRONTEND_NET_ZONES_ERROR_CODES.UPDATE_FAILED,
                'NetZones Page / Update Zones Request Error',
                error?.message || String(error)
            );
        } finally {
            setRefreshing(false);
        }
    };

    if (isLoading) {
        return (
            <Content currentMenu='netzones' headerTitle='Network Zones'>
                <div className={styles.loading}>Loading NetZones data...</div>
            </Content>
        );
    }

    if (error) {
        return (
            <Content currentMenu='netzones' headerTitle='Network Zones'>
                <NetworkError />
                <div className={styles.supportErrorCard}>
                    <strong>Support Error:</strong>
                    <code className={styles.supportErrorCode}>{supportErrorMessage || error}</code>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <button
                            type='button'
                            className={styles.copySupportErrorButton}
                            onClick={handleCopySupportError}
                        >
                            {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                        </button>
                        <button
                            type='button'
                            className={styles.copySupportErrorButton}
                            onClick={() => clearNetZonesSupportError(true)}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </Content>
        );
    }

    return (
        <Content currentMenu='netzones' headerTitle='Network Zones'>
            <div className={styles.netZonesContainer}>
                {supportErrorMessage && (
                    <div className={styles.supportErrorBanner}>
                        <div className={styles.supportErrorBannerText}>
                            <strong>Support Error:</strong>
                            <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
                        </div>
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <button
                                type='button'
                                className={styles.copySupportErrorButton}
                                onClick={handleCopySupportError}
                            >
                                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                            </button>
                            <button
                                type='button'
                                className={styles.copySupportErrorButton}
                                onClick={() => clearNetZonesSupportError(true)}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}
                <div className={styles.headerActions}>
                    <Link to='/net-zones/import' className={styles.importLink}>
                        <svg className={styles.importIcon}>
                            <use xlinkHref="/assets/sprite.svg#icon-plus" />
                        </svg>
                        Add NetZone
                    </Link>
                    <button
                        type='button'
                        className={styles.updateZonesButton}
                        onClick={handleRefreshZones}
                        disabled={refreshing}
                        title='Re-apply the current zones to devices already in the database'
                    >
                        {refreshing ? 'Updating…' : 'Update Zones'}
                    </button>
                    {refreshMsg && <span className={styles.updateZonesResult}>{refreshMsg}</span>}
                </div>

                {/* Company filter is provider-only. A client is locked to its own
                    company (the backend enforces it regardless), so no picker. */}
                {isProvider && (
                    <div className={styles.filterContainer}>
                        <label htmlFor="companyFilter">Filter by Company: </label>
                        <select id="companyFilter" value={selectedCompany} onChange={handleCompanyChange}>
                            <option value="ALL">All Companies</option>
                            {availableCompanies.map(company => (
                                <option key={company} value={company}>{company}</option>
                            ))}
                        </select>
                    </div>
                )}

                {netZonesData.length === 0 ? (
                    <p>No NetZones data available for the selected company.</p>
                ) : (
                    <table className={styles.netZonesTable}>
                        <thead>
                            <tr>
                                {columns.map(col => <th key={col}>{col}</th>)}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {netZonesData.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) => (
                                        <td key={`${rowIndex}-${cellIndex}`}>
                                            {columns[cellIndex] === 'Color' && cell ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                    <span style={{ width: 14, height: 14, borderRadius: '50%',
                                                        background: String(cell), border: '1px solid rgba(0,0,0,0.2)',
                                                        display: 'inline-block' }} />
                                                    <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.85em' }}>{String(cell)}</span>
                                                </span>
                                            ) : columns[cellIndex] === 'Guest' ? (
                                                (cell === true || cell === 'true' || cell === 'Yes') ? 'Yes' : '—'
                                            ) : (cell === null || cell === undefined ? 'N/A' : String(cell))}
                                        </td>
                                    ))}
                                    <td className={styles.actionsColumn}>
                                        <button 
                                            className={styles.editButton} 
                                            onClick={() => handleEdit(row, rowIndex)}
                                        >
                                            Edit
                                        </button>
                                        <button 
                                            className={styles.deleteButton} 
                                            onClick={() => handleDelete(row, rowIndex)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && editingZone && (
                <Modal show={showEditModal} onClose={() => setShowEditModal(false)}>
                    <ModalHeading>Edit NetZone</ModalHeading>
                    <EditZoneForm 
                        zone={editingZone} 
                        columns={columns}
                        onSave={handleUpdateZone}
                        onCancel={() => setShowEditModal(false)}
                    />
                </Modal>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && deletingZone && (
                <Modal show={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
                    <ModalHeading>Confirm Delete</ModalHeading>
                    <div className={styles.deleteConfirmation}>
                        <p>Are you sure you want to delete this NetZone?</p>
                        <div className={styles.modalActions}>
                            <button 
                                className={styles.cancelButton} 
                                onClick={() => setShowDeleteModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className={styles.confirmDeleteButton} 
                                onClick={handleDeleteZone}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </Content>
    );
};

// Edit Form Component
const EditZoneForm = ({ zone, columns, onSave, onCancel }) => {
    const [formData, setFormData] = useState({});

    useEffect(() => {
        
        // Initialize form data with the correct mapping
        const initialFormData = {
            ID: zone.ID,
            Company: zone.Company,
            StartIP: zone.StartIP,
            EndIP: zone.EndIP,
            Zone: zone.Zone,
            Guest: !!zone.Guest
        };
        
        setFormData(initialFormData);
    }, [zone, columns]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <form onSubmit={handleSubmit} className={styles.editForm}>
            <div className={styles.formGroup}>
                <label className={styles.formLabel}>Company</label>
                <input
                    type="text"
                    value={formData.Company || ''}
                    onChange={(e) => handleInputChange('Company', e.target.value)}
                    className={styles.formInput}
                    readOnly
                />
            </div>
            
            <div className={styles.formGroup}>
                <label className={styles.formLabel}>Start IP</label>
                <input
                    type="text"
                    value={formData.StartIP || ''}
                    onChange={(e) => handleInputChange('StartIP', e.target.value)}
                    className={styles.formInput}
                />
            </div>
            
            <div className={styles.formGroup}>
                <label className={styles.formLabel}>End IP</label>
                <input
                    type="text"
                    value={formData.EndIP || ''}
                    onChange={(e) => handleInputChange('EndIP', e.target.value)}
                    className={styles.formInput}
                />
            </div>
            
            <div className={styles.formGroup}>
                <label className={styles.formLabel}>Zone</label>
                <input
                    type="text"
                    value={formData.Zone || ''}
                    onChange={(e) => handleInputChange('Zone', e.target.value)}
                    className={styles.formInput}
                />
            </div>

            <div className={styles.formGroup}>
                <label className={styles.formLabel}>Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                        type="color"
                        value={formData.Color || '#176576'}
                        onChange={(e) => handleInputChange('Color', e.target.value)}
                        style={{ width: 48, height: 34, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                        disabled={(formData.Zone || '').trim() === 'External'}
                    />
                    <span style={{ fontFamily: 'ui-monospace, monospace' }}>{formData.Color || '#176576'}</span>
                    {(formData.Zone || '').trim() === 'External' && (
                        <span style={{ color: 'var(--grey, #6d6d6d)', fontSize: '0.85em' }}>External is always red</span>
                    )}
                </div>
            </div>

            <div className={styles.formGroup}>
                <label className={styles.formLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={!!formData.Guest}
                        onChange={(e) => handleInputChange('Guest', e.target.checked)}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                    />
                    Guest / uncontrolled network (e.g. guest wireless)
                </label>
                <span style={{ color: 'var(--grey, #6d6d6d)', fontSize: '0.85em' }}>
                    Denied-country geo from this zone is downgraded to Monitor (not a high alert),
                    and behavioural SOC findings from it drop to Low — kept visible, not paged.
                </span>
            </div>

            <div className={styles.modalActions}>
                <button type="button" className={styles.cancelButton} onClick={onCancel}>
                    Cancel
                </button>
                <button type="submit" className={styles.saveButton}>
                    Save
                </button>
            </div>
        </form>
    );
};

export default NetZones;
